import { useState } from 'react';

export function CTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4i161ttnoqs9bvm9u10', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="consultation" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-block mb-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-rose-100 rounded-full">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-calendar-check-line text-xl text-rose-800"></i>
                </div>
                <span className="text-sm font-semibold text-rose-800 uppercase tracking-wide">Book Consultation</span>
              </div>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 leading-tight" style={{ fontFamily: "'Cinzel', serif" }}>
              Start Your Journey to <strong className="text-rose-800">Clarity</strong>
            </h2>

            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Take the first step towards understanding your potential <strong className="text-rose-700">arranged marriage</strong> partner. Book a consultation session and gain the <strong className="text-rose-700">clarity</strong> you need to make informed decisions.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-rose-100 to-amber-100 rounded-xl flex-shrink-0">
                  <i className="ri-user-heart-line text-2xl text-rose-800"></i>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900 mb-1">Personalized Guidance</h3>
                  <p className="text-gray-600">Tailored sessions focused on your specific situation and needs</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-rose-100 to-amber-100 rounded-xl flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-rose-800"></i>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900 mb-1">Confidential & Safe</h3>
                  <p className="text-gray-600">Your privacy and comfort are our top priorities</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-rose-100 to-amber-100 rounded-xl flex-shrink-0">
                  <i className="ri-time-line text-2xl text-rose-800"></i>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900 mb-1">Flexible Scheduling</h3>
                  <p className="text-gray-600">Choose from various session frequencies that suit your timeline</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-gradient-to-br from-rose-50 via-amber-50/50 to-rose-50 rounded-3xl p-8 lg:p-10 border-2 border-rose-200">
            <form id="consultation-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-rose-200 focus:border-rose-500 focus:outline-none transition-colors text-sm"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-rose-200 focus:border-rose-500 focus:outline-none transition-colors text-sm"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-rose-200 focus:border-rose-500 focus:outline-none transition-colors text-sm"
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-gray-900 mb-2">
                  Service Interested In *
                </label>
                <div className="relative">
                  <select
                    id="service"
                    name="service"
                    required
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className="w-full px-4 py-3 pr-8 rounded-xl border-2 border-rose-200 focus:border-rose-500 focus:outline-none transition-colors appearance-none cursor-pointer text-sm"
                  >
                    <option value="">Select a service</option>
                    <option value="Monthly Review">1-on-1: Monthly Review</option>
                    <option value="Fortnightly Review">1-on-1: Fortnightly Review</option>
                    <option value="Weekly Review">1-on-1: Weekly Review</option>
                    <option value="Bi-weekly Review">1-on-1: Bi-weekly Review</option>
                    <option value="Process Course">Clarity Class: Process Course</option>
                    <option value="Nuanced Course">Clarity Class: Nuanced Course</option>
                    <option value="Detailed Course">Clarity Class: Detailed Course</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-xl text-gray-400 pointer-events-none"></i>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                  Message (Optional)
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-rose-200 focus:border-rose-500 focus:outline-none transition-colors resize-none text-sm"
                  placeholder="Tell us about your situation (max 500 characters)"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-rose-800 to-red-900 text-white rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Submitting...' : 'Book Consultation'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                  <p className="text-green-800 font-semibold text-center">Thank you! We'll contact you soon.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl">
                  <p className="text-red-800 font-semibold text-center">Something went wrong. Please try again.</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
